import type { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { Command, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { keymap } from '@atlaskit/editor-prosemirror/keymap';

import type { AIPlugin } from '../../aiPluginType';
import type { AIGlobalOptIn } from '../../types/types';
import { improveWriting } from '../../ui/prebuilt/config-items/prompts/enhance/enhance';
import { createOpenAIModalCommand } from '../decoration/actions';

import { hidePromptCommand, pluginKey } from './plugin-factory';
import { getStandDownManagerOptions, StandDownManager } from './stand-down-manager';

/**
 * Create a separate keymap plugin for the ai-quick-prompt feature.
 * Handles Tab (trigger) and Escape (dismiss) keys.
 *
 * @param options - The options
 * @param options.aiGlobalOptIn - AI opt-in status
 * @param options.api - The editor plugin API
 * @returns A ProseMirror Plugin instance
 */
export function createAiQuickPromptKeymapPlugin({
	api,
	aiGlobalOptIn,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
}): SafePlugin {
	const configItem = improveWriting();
	const standDownManager = new StandDownManager(getStandDownManagerOptions({ api }));

	const keymapObj: Record<string, Command> = {};

	// --- Tab handler ---
	keymapObj['Tab'] = (state, dispatch, view) => {
		const pluginState = pluginKey.getState(state);
		if (!pluginState?.isPromptVisible || !pluginState.eligibleNodeRange) {
			return false; // Pass through to next plugin
		}

		if (dispatch && view) {
			const range = pluginState.eligibleNodeRange;

			// - Hide the prompt
			hidePromptCommand(state, dispatch);

			// - Invoke the existing Improve Writing AI action
			const openAIModalCommand = createOpenAIModalCommand({
				state: state,
				configItem,
				lastTriggeredFrom: 'aiQuickPrompt',
				triggerMethod: 'shortcut',
				aiGlobalOptIn,
				overrideFromPos: range.from,
				overrideToPos: range.to,
			});
			openAIModalCommand(state, dispatch);
		}

		return true; // Handled
	};

	// --- Escape handler ---
	keymapObj['Escape'] = (state, dispatch) => {
		const pluginState = pluginKey.getState(state);
		if (!pluginState?.isPromptVisible) {
			return false; // Pass through to next plugin
		}

		if (dispatch) {
			// - Record dismissal for stand-down
			standDownManager.recordDismissal();

			// - Hide the prompt
			hidePromptCommand(state, dispatch);
		}

		return true; // Handled
	};

	return keymap(keymapObj) as SafePlugin;
}
